import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LockKey, ArrowRight, Spinner, ShieldCheck } from "@phosphor-icons/react";
import { setPortalSession, portalErr } from "@/lib/portalApi";

export default function PortalLogin({ token, info, logoUrl, api, onSuccess }) {
  const [nachname, setNachname] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const k = info.kanzlei || {};

  const submit = async (e) => {
    e.preventDefault();
    if (!nachname.trim()) { setError("Bitte geben Sie Ihren Nachnamen ein"); return; }
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/portal/public/${token}/login`, { nachname: nachname.trim() });
      setPortalSession(token, r.data.session_token, remember);
      onSuccess(r.data);
    } catch (err) {
      setError(portalErr(err, "Anmeldung fehlgeschlagen"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bg-[#1A2238] text-white pt-10 pb-20 px-4">
        <div className="max-w-md mx-auto text-center">
          {logoUrl ? (
            <span className="inline-block bg-white rounded-xl px-4 py-3 mb-5 shadow-lg"><img src={logoUrl} alt={k.name} className="h-12 max-w-[200px] object-contain" /></span>
          ) : null}
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">Mandantenportal</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight" data-testid="portal-login-kanzlei">{k.name || "Ihre Kanzlei"}</h1>
          {k.anwalt && <p className="text-sm text-white/65 mt-2">{k.anwalt}</p>}
        </div>
      </div>

      <div className="max-w-md mx-auto w-full px-4 -mt-12 pb-12">
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(10,25,47,0.12)] border border-[#E5E7EB] p-6 sm:p-8 space-y-5" data-testid="portal-login-form">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#9CA3AF]">Ihre Akte</p>
              <p className="font-mono text-lg text-[#1A2238]" data-testid="portal-login-aktenzeichen">{info.aktenzeichen}</p>
            </div>
            <span className="h-11 w-11 rounded-full bg-[#F4F3EF] flex items-center justify-center text-[#1A2238]"><LockKey size={22} weight="duotone" /></span>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0A192F]" htmlFor="portal-nachname">Nachname</label>
            <Input
              id="portal-nachname"
              autoFocus
              autoComplete="family-name"
              value={nachname}
              onChange={(e) => setNachname(e.target.value)}
              placeholder="z. B. Müller"
              className="mt-1.5 h-12 text-base"
              data-testid="portal-login-nachname"
            />
            <p className="text-xs text-[#6B7280] mt-1.5">Zur Sicherheit bestätigen Sie bitte Ihren Nachnamen, wie er Ihrer Kanzlei vorliegt.</p>
          </div>
          {error && <p className="text-sm text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2" data-testid="portal-login-error">{error}</p>}
          <label className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-[#1A2238]" data-testid="portal-login-remember" />
            Auf diesem Gerät angemeldet bleiben
          </label>
          <Button type="submit" disabled={busy} className="w-full h-12 text-base bg-[#1A2238] hover:bg-[#2C3859] rounded-xl" data-testid="portal-login-submit">
            {busy ? <Spinner size={18} className="animate-spin mr-2" /> : <ArrowRight size={18} className="mr-2" />} Portal öffnen
          </Button>
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-[#9CA3AF]"><ShieldCheck size={14} weight="duotone" /> Verschlüsselte Verbindung · Persönlicher Zugangslink</p>
        </form>
        {(k.telefon || k.email) && (
          <p className="text-center text-xs text-[#6B7280] mt-6">
            Fragen? {k.telefon && <a href={`tel:${k.telefon}`} className="underline">{k.telefon}</a>}{k.telefon && k.email ? " · " : ""}{k.email && <a href={`mailto:${k.email}`} className="underline">{k.email}</a>}
          </p>
        )}
      </div>
    </div>
  );
}
